import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";
import { STEPS } from "@/sections/Method";

const DETAILS = [
  "The first conversation is a mirror. We slow the loop down until the pattern becomes visible — the same argument, the same exit, the same ceiling, wearing new clothing. Nothing changes until it's named.",
  "Every pattern was once protection. We trace it to the root — where it started, what it kept safe, why the nervous system still reaches for it decades later. Understanding removes the shame; seeing removes the power.",
  "Insight alone doesn't put the weight down. This is the somatic part: breath, stillness, and the body finally standing down from a threat that passed years ago. Release is physical before it is intellectual.",
  "The crack becomes the seam. We turn what happened into meaning you can carry — not a story you hide, a line of gold you chose. This is kintsugi applied to a life.",
  "Change becomes analog: small, deliberate, repeatable. New choices practiced in real rooms with real people, until the new pattern feels like it was always yours.",
];

export default function Approach() {
  return (
    <main className="pt-16" data-testid="approach-page">
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(169,129,47,0.105), transparent 62%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="eyebrow mb-6">The Approach</p>
            <h1
              className="font-display text-ink font-medium leading-[1.08] max-w-4xl"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.4rem)" }}
              data-testid="approach-heading"
            >
              You can't reframe what you refuse to <em className="italic text-goldink">see</em> first.
            </h1>
            <p className="mt-8 text-inkdim leading-relaxed max-w-2xl text-[15px] md:text-base">
              The T.A.L.K. Method™ is the sequence underneath everything Rajdeep does —
              private work, corporate rooms, circles. Five movements, always in order,
              because each one only works after the one before it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.05}>
              <div className="grid md:grid-cols-12 gap-5 md:gap-10 py-12 md:py-16 border-t border-[rgba(169,129,47,0.26)]" data-testid={`approach-step-${s.n}`}>
                <span className="md:col-span-1 text-[11px] tracking-[0.3em] text-gold/70 pt-2">{s.n}</span>
                <h2 className="md:col-span-4 font-display text-3xl md:text-5xl text-ink font-medium">{s.name}</h2>
                <div className="md:col-span-7">
                  <p className="font-display italic text-goldink text-xl md:text-2xl leading-snug mb-5">{s.line}</p>
                  <p className="text-inkdim leading-relaxed text-[15px] max-w-xl">{DETAILS[i]}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-[rgba(169,129,47,0.26)]" />
        </div>
      </section>

      <section className="py-24 md:py-32 bg-paper">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="eyebrow mb-6">The principle</p>
            <h2 className="font-display text-ink font-medium leading-[1.15] text-3xl sm:text-4xl md:text-5xl" data-testid="approach-vertical-heading">
              Most work moves sideways. This work moves <em className="italic text-gold">down</em>.
            </h2>
            <p className="mt-8 text-inkdim leading-relaxed max-w-xl mx-auto text-[15px]">
              New tactics, new habits, new resolutions — sideways motion. The vertical goes
              to the root: the moment the pattern was born, and the protection it once was.
              Touch the root and the whole structure shifts at once.
            </p>
            <GoldSeam variant="horizontal" className="mx-auto mt-12 w-64 h-10 opacity-60" delay={0.2} />
            <Link
              to="/contact"
              data-testid="approach-cta-button"
              className="inline-block mt-12 bg-deep text-canvas text-xs uppercase tracking-[0.28em] font-semibold px-9 py-4 hover:bg-gold transition-colors duration-300"
            >
              Start a T.A.L.K.
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
